/* lp-pdp.js — the product page's behaviour: the variant chips (size or insert) drive the price and
   the add-to-bag button, add-to-bag goes through lp-cart.js (Horizon's own cart event), the gallery
   layout's thumbnails swap the main image (click, or a 250ms hover on desktop) and get swipe dots
   on phones. Ported 17 Sep 2026 from the prototype's openProduct/swapGal/galleryDots/nearestSlide
   and the swatch hover-select; the variant chips are the one addition Shopify needs — the
   prototype's product page sold whichever size the collection shelf last had selected. */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('lp-pdp')) return;
  var HOVER_MS = 250;
  function money(cents) { var n = cents / 100; return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2)); }
  function nearestSlide(track) {
    /* the strip's direct children: <img>s in the gallery, <figure>s in the stack (an <img> inside a
       figure reports offsetLeft 0, which would make every slide look like the first) */
    var center = track.scrollLeft + track.clientWidth / 2, best = 0, bd = 1e9, ch = track.children;
    for (var i = 0; i < ch.length; i++) { var c = ch[i].offsetLeft + ch[i].offsetWidth / 2, d = Math.abs(c - center); if (d < bd) { bd = d; best = i; } }
    return best;
  }

  class LPPdp extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      var self = this, unbind = [];
      function on(t, ev, fn, opt) { t.addEventListener(ev, fn, opt); unbind.push(function () { t.removeEventListener(ev, fn, opt); }); }
      this._unbind = function () { unbind.forEach(function (f) { f(); }); };
      this.hoverT = null;
      on(this, 'click', function (e) {
        var t = e.target, el;
        if (!t || !t.closest) return;
        if ((el = t.closest('[data-variant-pick]')) && self.contains(el)) { if (!el.disabled) self.selectVariant(el); return; }
        if ((el = t.closest('[data-atc]')) && self.contains(el)) { self.addBag(el); return; }
        if ((el = t.closest('.gthumbs img')) && self.contains(el)) { self.swapGal(el); return; }
      });
      if (window.matchMedia && matchMedia('(hover:hover)').matches) {
        on(this, 'mouseover', function (e) {
          var t = e.target; if (!t || t.tagName !== 'IMG' || !t.closest) return;
          var strip = t.closest('.gthumbs'); if (!strip || !self.contains(strip)) return;
          self.clearHover();
          if (t.classList.contains('on')) return;
          self.hoverT = setTimeout(function () { self.hoverT = null; self.swapGal(t); }, HOVER_MS);
        });
        on(this, 'mouseout', function (e) { if (e.target && e.target.tagName === 'IMG') self.clearHover(); });
      }
      /* the gallery's thumbnail strip, or — on phones, where the stack becomes a strip too — the stack */
      var track = this.querySelector('.gthumbs') || this.querySelector('.pdpstack');
      if (track) this.galleryDots(track, on);
      this.stickyBar(on, unbind);
    }
    /* 19 Oct 2026, phone fixes: the bottom bar shows on phones while the real add to bag is out of view, and its own
       button adds exactly what the real one would (same variant, same lp-cart.js add, same drawer). */
    stickyBar(on, unbind) {
      var bar = this.querySelector('[data-lpsticky]'), real = this.querySelector('[data-atc]');
      if (!bar || !real || !('IntersectionObserver' in window) || !window.matchMedia) return;
      /* 900px matches where the stack layout turns one column. On two-size products the bar waits until the shopper has
         scrolled past the real button (they have seen the size choice by then); one-size products get it from the first
         screen. The header (~60px) doesn't count as "in view". After the 19 Oct 2026 review. */
      var self = this, mq = window.matchMedia('(max-width: 900px)'), realVisible = true;
      var sized = bar.hasAttribute('data-sticky-sized');
      var head = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 60;
      function sync() { bar.hidden = !(mq.matches && !realVisible); document.body.classList.toggle('lp-has-sticky', !bar.hidden); }
      var io = new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        /* passed = gone up under the header line; on two-size products a button still below the fold counts as
           "not reached yet", so the bar waits (round 3: judging by top > 0 missed slow scrolls) */
        var line = e.rootBounds ? e.rootBounds.top : head;
        realVisible = e.isIntersecting || (sized && e.boundingClientRect.bottom > line + 1);
        sync();
      }, { threshold: 0, rootMargin: '-' + head + 'px 0px 0px 0px' });
      io.observe(real);
      unbind.push(function () { io.disconnect(); document.body.classList.remove('lp-has-sticky'); });
      /* the observer only fires when the button crosses an edge, so a jump that skips over it (find-in-page, a link to
         an anchor) would leave the bar stale: a cheap scroll check applies the same rule (round 4, 19 Oct 2026) */
      var raf = 0;
      on(window, 'scroll', function () {
        if (raf || !mq.matches) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          var r = real.getBoundingClientRect();
          realVisible = (r.bottom > head && r.top < window.innerHeight) || (sized && r.bottom > head + 1);
          sync();
        });
      }, { passive: true });
      if (mq.addEventListener) on(mq, 'change', sync);
      on(bar, 'click', function (e) {
        var b = e.target && e.target.closest ? e.target.closest('[data-sticky-atc]') : null;
        if (b) self.addBag(b);
      });
    }
    disconnectedCallback() { if (this._unbind) { this._unbind(); this._unbind = null; } this.clearHover(); }
    clearHover() { if (this.hoverT) { clearTimeout(this.hoverT); this.hoverT = null; } }
    selectVariant(chip) {
      this.querySelectorAll('[data-variant-pick]').forEach(function (c) { c.classList.toggle('on', c === chip); });
      var price = this.querySelector('[data-price]'); if (price && chip.dataset.price) price.textContent = money(parseInt(chip.dataset.price, 10));
      var atc = this.querySelector('[data-atc]'); if (atc) atc.setAttribute('data-variant', chip.dataset.variantPick);
      /* 19 Oct 2026: the product form's id (the wallet button buys this) and the phone bar follow the chosen size */
      var idIn = this.querySelector('[data-lp-variant-input]'); if (idIn) idIn.value = chip.dataset.variantPick;
      var sAtc = this.querySelector('[data-sticky-atc]'); if (sAtc) sAtc.setAttribute('data-variant', chip.dataset.variantPick);
      var sPrice = this.querySelector('[data-sticky-price]'); if (sPrice && chip.dataset.price) sPrice.textContent = money(parseInt(chip.dataset.price, 10));
      var sSize = this.querySelector('[data-sticky-size]'), sizeWord = (chip.textContent || '').split('\u00b7')[0].trim();
      if (sSize) sSize.textContent = ' \u00b7 ' + sizeWord;
      if (sAtc && sSize) { var nm = this.querySelector('.lpsticky__name'); var base = nm ? nm.firstChild.textContent.trim() : ''; sAtc.setAttribute('aria-label', 'Add ' + base + ', ' + sizeWord + ', to your bag'); }
      /* keep the URL honest for sharing and the back button, as Shopify's own forms do */
      try { var u = new URL(window.location.href); u.searchParams.set('variant', chip.dataset.variantPick); history.replaceState(null, '', u.toString()); } catch (e) {}
    }
    addBag(btn) {
      var id = btn.getAttribute('data-variant'); if (!id) return;
      var self = this;
      btn.classList.remove('pop'); void btn.offsetWidth; btn.classList.add('pop');
      setTimeout(function () { btn.classList.remove('pop'); }, 700);
      /* same confirmation the collection shelf gives: the button dips, then confetti in the product's own
         colour (the prototype used the collection's palette; on a single product page its own hex is the
         honest equivalent) */
      if (window.lpConfetti) {
        var hex = this.dataset.hex, hex2 = this.dataset.hex2;
        window.lpConfetti(btn, [hex, hex2]);
      }
      var add = (window.LPCart && window.LPCart.add) ? Promise.resolve(window.LPCart.add(id, 1, this.dataset.productId)) : (window.lpCartAdd ? window.lpCartAdd(this, id, 1, this.dataset.productId) : Promise.reject(new Error('lp-cart.js is not loaded')));
      add.catch(function (err) { if (window.console) console.error('lp-pdp: add to bag failed', err); });
    }
    swapGal(el) {
      var main = this.querySelector('.gmain'); if (!main) return;
      main.src = el.currentSrc || el.src; main.alt = el.alt || main.alt;
      var t = el.parentElement.children; for (var i = 0; i < t.length; i++) t[i].classList.remove('on');
      el.classList.add('on');
    }
    galleryDots(track, on) {
      var n = track.children.length;
      var dots = track.nextElementSibling;
      if (!dots || !dots.classList.contains('swipedots')) { dots = document.createElement('div'); dots.className = 'swipedots'; track.parentNode.insertBefore(dots, track.nextSibling); }
      var html = ''; for (var i = 0; i < n; i++) html += '<span' + (i === 0 ? ' class="on"' : '') + '></span>'; dots.innerHTML = html;
      dots.style.display = n > 1 ? '' : 'none';
      track.scrollLeft = 0;
      on(track, 'scroll', function () { var idx = nearestSlide(track), c = dots.children; for (var j = 0; j < c.length; j++) c[j].classList.toggle('on', j === idx); }, { passive: true });
    }
  }
  customElements.define('lp-pdp', LPPdp);
})();
